/**
 * `auth` endpoint wrappers.
 *
 * Request/response shapes verified field-by-field against
 * refactor/backend/src/modules/auth/routes.ts and
 * refactor/backend/src/modules/users/serializers.ts — citations inline.
 */
import { apiClient } from '../client';
import {
  AUTH_CHANGE_PASSWORD,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_ME,
  AUTH_REGISTER,
  AUTH_USERS,
} from './paths';
import type { Paginated } from '../types';
import type { User, UserProfile, UserRole } from '../../types/user';

/**
 * `loginSchema` — backend src/modules/users/serializers.ts:207-210:
 *     z.object({ email: z.string().min(1), password: z.string().min(1) })
 *
 * The credential field is `email`, NOT `username`: Django sets
 * `USERNAME_FIELD = 'email'` (users/models.py:24).
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Response of `POST auth/login/` — backend src/modules/auth/routes.ts:227-231,
 * `res.status(200).json({ refresh, access, user })`.
 */
export interface LoginResponse {
  refresh: string;
  access: string;
  user: User;
}

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>(AUTH_LOGIN, credentials);
  return response.data;
}

/**
 * `registerSchema` — backend src/modules/users/serializers.ts:177-199.
 *
 * Required: email, username, password, password2, first_name, last_name.
 * Optional: role (ignored for public self-registration — an unauthenticated
 * caller is forced to TENANT at routes.ts:154-158, the request is not
 * rejected), phone (nullable, normalised to ''), property_id, unit_id.
 *
 * `password2` must equal `password` or the schema fails with Django's exact
 * message, "Password fields didn't match." (serializers.ts:190-199).
 *
 * `property_id` is accepted and then never used — dead input in Django too
 * (see the resolved register/PENDING-lease entry in PARITY_NOTES.md). `unit_id`
 * on a TENANT registration creates a PENDING lease as a side effect.
 */
export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  password2: string;
  first_name: string;
  last_name: string;
  role?: string | undefined;
  phone?: string | null;
  property_id?: number | undefined;
  unit_id?: number | undefined;
}

/**
 * Response of `POST auth/register/` — backend src/modules/auth/routes.ts:183-187,
 * `res.status(201).json({ user, refresh, access })`.
 *
 * Same three keys as login, declared in a different order. Key ORDER is not part
 * of the contract; the key SET is.
 */
export interface RegisterResponse {
  user: User;
  refresh: string;
  access: string;
}

export async function register(payload: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post<RegisterResponse>(AUTH_REGISTER, payload);
  return response.data;
}

/**
 * `POST auth/logout/` — backend src/modules/auth/routes.ts:265-286.
 *
 * Requires BOTH a valid access token (`requireAuth`) and the refresh token in
 * the body, which it blacklists. Idempotent: logging out twice succeeds twice.
 */
export async function logout(refresh: string): Promise<void> {
  await apiClient.post(AUTH_LOGOUT, { refresh });
}

/** `GET auth/me/` — the same `UserSerializer` payload login embeds. */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>(AUTH_ME);
  return response.data;
}

/**
 * `UpdateProfileSerializer` request body — users/serializers.ts:253-259. Every
 * field is optional for PATCH; PUT additionally requires `profile` to be
 * present (users/routes.ts:236-241) — the caller must build a payload with
 * `profile` populated when passing `partial: false`.
 */
export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  profile?: Partial<UserProfile>;
  /** Node-only, beyond Django parity — see types/user.ts's `User.kra_pin` doc. `''`/null clears it. */
  kra_pin?: string | null;
}

/**
 * Response of `PATCH|PUT auth/me/` — users/routes.ts:225-229,283-288. NOT the
 * full `UserSerializer`: a narrow `{first_name, last_name, phone, profile}`
 * object. Do not reuse `User` for this — the shape is genuinely smaller.
 */
export interface UpdateProfileResponse {
  first_name: string;
  last_name: string;
  phone: string;
  profile: UserProfile | null;
  /** Only present in the response when `kra_pin` was sent in the request — see users/routes.ts's updateOwnProfile. */
  kra_pin?: string | null;
}

/**
 * `PATCH|PUT auth/me/`. Defaults to PATCH (partial update) — the form built on
 * top of this always sends every field it knows about anyway, so there is no
 * caller that needs the stricter PUT (`profile` required) today, but the
 * option is kept since the backend routes both.
 */
export async function updateProfile(
  payload: UpdateProfileRequest,
  partial = true,
): Promise<UpdateProfileResponse> {
  const response = partial
    ? await apiClient.patch<UpdateProfileResponse>(AUTH_ME, payload)
    : await apiClient.put<UpdateProfileResponse>(AUTH_ME, payload);
  return response.data;
}

/**
 * `POST auth/change-password/` — users/routes.ts:312-339. A wrong
 * `old_password` returns `{old_password: ["Wrong password."]}` at the TOP
 * level of the 400 body, NOT the `{errors: {field: [...]}}` shape
 * `parseApiError` reads — callers need to check for that shape explicitly.
 */
export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  new_password2: string;
}

export async function changePassword(payload: ChangePasswordRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(AUTH_CHANGE_PASSWORD, payload);
  return response.data;
}

/**
 * `GET auth/` — users/routes.ts:385-420. Role-scoped server-side
 * (`userListScope`): SUPERAGENT sees everyone, LANDLORD/AGENT see a real but
 * narrower list (self + their tenants/agents), TENANT sees only themselves.
 * `?role=` is applied verbatim by the backend — an unrecognised value yields
 * an empty page, not a 400.
 *
 * `role_counts` is new capability beyond Django parity — see the backend
 * route's own comment. It always reflects the caller's full scope,
 * independent of whatever `?role=` filter is active on this same request.
 */
export interface UserListResponse extends Paginated<User> {
  role_counts: Record<UserRole, number>;
}

export async function listUsers(params?: {
  page?: number | undefined;
  role?: string | undefined;
}): Promise<UserListResponse> {
  const response = await apiClient.get<UserListResponse>(AUTH_USERS, { params });
  return response.data;
}

/**
 * `PATCH auth/{id}/` — NEW, no Django route (see PARITY_NOTES.md).
 * SUPERAGENT-only on the backend; the only writable fields are `role` and
 * `is_active` (everything else an account owner manages themselves via
 * `PATCH auth/me/`).
 */
export interface UserAdminPatchRequest {
  role?: UserRole;
  is_active?: boolean;
}

export async function updateUserAdmin(id: number, payload: UserAdminPatchRequest): Promise<User> {
  const response = await apiClient.patch<User>(`${AUTH_USERS}${id}/`, payload);
  return response.data;
}

/**
 * `DELETE auth/{id}/` — NEW, no Django route (see PARITY_NOTES.md).
 * SUPERAGENT-only; soft-deletes (`isActive: false`) rather than a hard
 * delete, so the response is an empty 204 like every other delete endpoint.
 */
export async function deactivateUser(id: number): Promise<void> {
  await apiClient.delete(`${AUTH_USERS}${id}/`);
}
